/**
 * English copy for shop → player detail wallet history (wallet_transactions API).
 */

import type { ShopWalletLedgerRow } from '../types';

/** Merges root-level wallet tx fields with metadata (new API puts several keys at root). */
export function mergePlayerWalletTxMeta(row: ShopWalletLedgerRow): Record<string, unknown> {
  const m =
    row.metadata && typeof row.metadata === 'object'
      ? { ...(row.metadata as Record<string, unknown>) }
      : {};
  return {
    ...m,
    sourceChannel: row.sourceChannel ?? m.sourceChannel,
    payerWallet: row.payerWallet ?? m.payerWallet,
    recipientWallet: row.recipientWallet ?? m.recipientWallet,
    referenceId: row.referenceId ?? m.referenceId,
    referenceType: row.referenceType ?? m.referenceType,
    businessTransactionId: row.businessTransactionId ?? m.businessTransactionId,
    platformFeeRate: row.platformFeeRate ?? row.platform_fee_rate ?? m.platformFeeRate ?? m.platform_fee_rate,
    platformFeeAmount: row.platformFeeAmount ?? row.platform_fee_amount ?? m.platformFeeAmount ?? m.platform_fee_amount,
    costRate: row.costRate ?? row.cost_rate ?? m.costRate ?? m.cost_rate,
    costAmount: row.costAmount ?? row.cost_amount ?? m.costAmount ?? m.cost_amount
  };
}

function num(v: unknown): number {
  if (v === undefined || v === null) return 0;
  const n = typeof v === 'string' ? Number.parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : 0;
}

function fmtAmt(n: number): string {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 });
}

function fmtRatePct(rate: number): string {
  if (!Number.isFinite(rate) || rate < 0) return '—';
  return `${(rate * 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
}

export function playerWalletTxTitle(meta: Record<string, unknown>, rowType: string): string {
  const ek = String(meta.entryKind || '');
  switch (ek) {
    case 'transfer_from_shop':
      return 'Credits added from Shop (cash / Shop flow)';
    case 'card_credit_from_shop':
      return 'Credits added from Shop (card — per card workflow)';
    case 'redeem_to_shop':
      return 'Credits redeemed from Player to Shop wallet';
    case 'user_payment_deposit':
      return 'Player top-up via web / kiosk (after successful payment)';
    case 'hierarchical_credit_from_shop':
      return 'Credits issued — debited from Shop wallet';
    case 'hierarchical_credit_from_agent':
      return 'Credits issued — debited from Agent wallet';
    case 'hierarchical_credit_from_super_agent':
      return 'Credits issued — debited from Super Agent wallet';
    case 'credit_to_player':
      return 'Credit to Player wallet';
    default: {
      const t = String(rowType || '').toLowerCase();
      if (t === 'distribution') return 'Transfer / credit allocation';
      return String(rowType || 'activity').replace(/_/g, ' ') || 'Transaction';
    }
  }
}

/** Which wallet was debited to fund the player. */
export function playerWalletTxPayerLine(meta: Record<string, unknown>): string | null {
  const pw = meta.payerWallet as { ownerType?: string } | null | undefined;
  if (!pw?.ownerType) {
    const ek = String(meta.entryKind || '');
    if (ek === 'user_payment_deposit') {
      return 'Debit source: payment gateway (ledger deposit — Shop wallet not debited on this line).';
    }
    return null;
  }
  switch (pw.ownerType) {
    case 'shop':
      return 'Debit source: Shop wallet';
    case 'agent':
      return 'Debit source: Agent wallet';
    case 'super_agent':
      return 'Debit source: Super Agent wallet';
    case 'player':
      return 'Debit source: Player wallet (adjustment / transfer)';
    default:
      return `Debit source: ${pw.ownerType}`;
  }
}

/** Gateway platform fee only — shop cost rate is omitted here (internal pricing; confuses staff). */
export function playerWalletTxFeeLines(meta: Record<string, unknown>): { label: string; value: string }[] {
  const out: { label: string; value: string }[] = [];
  const pf = num(meta.platformFeeAmount ?? meta.platform_fee_amount);
  const pfr = num(meta.platformFeeRate ?? meta.platform_fee_rate);
  const hasPlatformAmt = pf > 1e-12;
  const hasPlatformRate = pfr > 1e-12;

  if (hasPlatformAmt) {
    out.push({
      label: 'Platform fee (USD)',
      value: hasPlatformRate ? `$${fmtAmt(pf)} (${fmtRatePct(pfr)})` : `$${fmtAmt(pf)}`
    });
  } else if (hasPlatformRate) {
    out.push({
      label: 'Platform fee rate',
      value: fmtRatePct(pfr)
    });
  }

  return out;
}
